/**
 * Discharge Summaries hooks
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api-client';
import { toast } from 'sonner';

// ==================== Types ====================

export interface DischargeSummary {
  id: number;
  patient_id: number;
  hospital_name: string;
  admission_date: string | null;
  discharge_date: string | null;
  diagnosis: string;
  procedures_performed?: string | null;
  medications_prescribed?: string | null;
  follow_up_instructions?: string | null;
  restrictions?: string | null;
  doctor_name?: string | null;
  doctor_phone?: string | null;
  emergency_contact?: string | null;
  notes?: string | null;
  created_at: string | null;
}

export interface DischargeSummaryCreate {
  patient_id: number;
  hospital_name: string;
  admission_date: string;
  discharge_date: string;
  diagnosis: string;
  procedures_performed?: string;
  medications_prescribed?: string;
  follow_up_instructions?: string;
  restrictions?: string;
  doctor_name?: string;
  doctor_phone?: string;
  emergency_contact?: string;
  notes?: string;
}

export interface DischargeSummaryListResponse {
  summaries: DischargeSummary[];
  count: number;
}

// ==================== API Functions ====================

/**
 * Get discharge summaries (optionally filtered by patient)
 */
async function fetchDischargeSummaries(patientId?: number): Promise<DischargeSummaryListResponse> {
  const url = patientId
    ? `/discharge-summaries?patient_id=${patientId}`
    : '/discharge-summaries';
  const response = await apiClient.get<DischargeSummaryListResponse>(url);
  return response;
}

/**
 * Helper function to fetch a single discharge summary
 */
async function fetchDischargeSummary(summaryId: number): Promise<DischargeSummary> {
  const response = await apiClient.get(`/discharge-summaries/${summaryId}`);
  return response;
}

/**
 * Helper function to create a discharge summary
 */
async function createDischargeSummary(summary: DischargeSummaryCreate): Promise<DischargeSummary> {
  const response = await apiClient.post('/discharge-summaries', summary);
  return response;
}

// ==================== React Query Hooks ====================

/**
 * Hook to get discharge summaries (all or filtered by patient)
 */
export function useDischargeSummaries(patientId?: number) {
  return useQuery({
    queryKey: ['discharge-summaries', patientId],
    queryFn: () => fetchDischargeSummaries(patientId),
  });
}

/**
 * Hook to get a specific discharge summary
 */
export function useDischargeSummary(summaryId: number) {
  return useQuery({
    queryKey: ['discharge-summary', summaryId],
    queryFn: () => fetchDischargeSummary(summaryId),
    enabled: !!summaryId,
  });
}

/**
 * Hook to create a new discharge summary
 */
export function useCreateDischargeSummary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDischargeSummary,
    onSuccess: () => {
      queryClient.invalidateQueries(['discharge-summaries']);
      toast.success('Discharge summary created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create discharge summary');
    },
  });
}